import CustomModal from "components/Common/CustomModal";
import React, { useRef, useState } from "react";
import { inputStyle } from "./CreateOrder";
import { ReactComponent as Upload } from "../../assets/img/uploadfile.svg";
import api from "api";
import { toast } from "react-toastify";
import { getMediaUrl } from "config";

const labelStyle = {
    marginTop: "10px",
    fontWeight: 600,
    fontSize: "16px",
    fontFamily: "Outfit",
    color: "#1C1C1C",
};

const hintStyle = {
    color: "#828282",
    fontSize: "13px",
    fontWeight: 400,
    fontFamily: "Outfit",
    display: "block",
    marginTop: "4px",
};

const EditBatch = ({ handleModal, selectedBatch, getOrders }) => {
    const [file, setFile] = useState();
    const [loading, setLoading] = useState(false);
    const uploadref = useRef(null);
    const isPending = selectedBatch?.status === "pending" || !selectedBatch?.status;
    const isLocked = !isPending;

    const lockedStyle = {
        ...inputStyle,
        opacity: isLocked ? 0.6 : 1,
        cursor: isLocked ? "not-allowed" : "text",
    };

    const [data, setData] = useState({
        number_of_codes: selectedBatch?.number_of_codes ?? "",
        generated_code_by_increase: selectedBatch?.generated_code_by_increase ?? "",
        rewardPoints: selectedBatch?.rewardPoints ?? "",
        productName: selectedBatch?.product_name ?? "",
        coaLink: selectedBatch?.coaLink ?? "",
        description: selectedBatch?.text ?? "",
    });

    const handleUpdateBatch = () => {
        // COA Link URL validation
        if (data.coaLink && !/^https?:\/\//.test(data.coaLink)) {
            return toast.error("COA Link must be a valid URL (starting with http:// or https://)");
        }

        setLoading(true);
        const formdata = new FormData();
        formdata.append("product_name", data.productName);
        formdata.append("rewardPoints", data.rewardPoints);
        formdata.append("product_img", file ?? selectedBatch?.product_img);
        formdata.append("text", data.description);
        formdata.append("generated_code_by_increase", data.generated_code_by_increase);
        formdata.append("number_of_codes", data.number_of_codes);
        formdata.append("coaLink", data.coaLink);

        api("put", `/requests/${selectedBatch._id}`, formdata)
            .then((res) => {
                setLoading(false);
                toast.success(res.message);
                handleModal();
                getOrders();
            })
            .catch((err) => {
                setLoading(false);
                console.log(err);
            });
    };

    const handleChange = (e) => {
        if (["rewardPoints", "generated_code_by_increase", "number_of_codes"].includes(e.target.name)) {
            setData((prev) => ({
                ...prev,
                [e.target.name]: Number(e.target.value.replace(/\D/g, "")),
            }));
        } else {
            setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files.length) {
            setFile(event.target.files[0]);
        } else {
            setFile();
        }
    };

    return (
        <CustomModal
            handleSave={handleUpdateBatch}
            heading="Edit Batch"
            buttonName="Save"
            handleModal={handleModal}
            loading={loading}
        >
            {/* ── Product Image ── */}
            <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                style={{ display: "none" }}
                ref={uploadref}
                accept="image/*"
            />
            <label style={labelStyle}>Product Image</label>
            <br />
            {!file && !selectedBatch?.product_img ? (
                <label
                    className="d-flex justify-content-center align-items-center"
                    htmlFor="fileUpload"
                    style={{
                        backgroundColor: "#F3F3F3",
                        border: "2px dashed #E4E4E4",
                        borderRadius: "16px",
                        fontFamily: "Outfit",
                        width: "100%",
                        height: "120px",
                        cursor: "pointer",
                    }}
                >
                    <div className="d-flex flex-column justify-content-center align-items-center">
                        <Upload />
                        <span className="mt-1">Upload Image</span>
                    </div>
                </label>
            ) : !file && selectedBatch?.product_img ? (
                <div
                    style={{ height: "120px", cursor: "pointer" }}
                    onClick={() => uploadref.current.click()}
                    className="d-flex justify-content-center"
                >
                    <img style={{ objectFit: "cover" }} src={getMediaUrl(selectedBatch?.product_img)} alt="" />
                </div>
            ) : (
                <div
                    style={{ height: "120px", cursor: "pointer" }}
                    onClick={() => uploadref.current.click()}
                    className="d-flex justify-content-center"
                >
                    <img style={{ objectFit: "cover" }} src={URL.createObjectURL(file)} alt="" />
                </div>
            )}

            {/* ── Product Name ── */}
            <label style={labelStyle}>Product Name</label>
            <br />
            <div className="text-right">
                <input
                    onChange={handleChange}
                    name="productName"
                    value={data.productName}
                    placeholder="Enter product name"
                    style={inputStyle}
                    type="text"
                    maxLength={120}
                />
                <span style={{ ...hintStyle, textAlign: "right" }}>
                    {data.productName?.length || 0}/120
                </span>
            </div>

            {/* ── Allocated Codes ── */}
            <label style={labelStyle}>
                Allocated Codes
                <span style={hintStyle}>Number of unique codes to allocate for this batch</span>
            </label>
            <input
                onChange={handleChange}
                name="number_of_codes"
                value={data.number_of_codes}
                placeholder="e.g. 10000"
                style={lockedStyle}
                type="text"
                disabled={isLocked}
            />

            {/* ── Generated Code Count ── */}
            <label style={labelStyle}>
                Generated Code Count
                <span style={hintStyle}>Total codes to generate for printing (extra copies for factory waste/misprints)</span>
            </label>
            <input
                onChange={handleChange}
                name="generated_code_by_increase"
                value={data.generated_code_by_increase}
                placeholder="e.g. 15000"
                style={lockedStyle}
                type="text"
                maxLength={24}
                disabled={isLocked}
            />

            {/* ── Reward Points ── */}
            <label style={labelStyle}>
                Reward Points
                <span style={hintStyle}>Points awarded to consumer per scan (0 = disabled)</span>
            </label>
            <input
                onChange={handleChange}
                name="rewardPoints"
                value={data.rewardPoints}
                placeholder="0"
                style={inputStyle}
                maxLength={3}
                type="text"
            />

            {/* ── COA Link ── */}
            <label style={labelStyle}>
                COA Link
                <span style={hintStyle}>Certificate of Analysis URL shown to consumers after scan</span>
            </label>
            <input
                onChange={handleChange}
                name="coaLink"
                value={data.coaLink}
                placeholder="https://example.com/coa.pdf"
                style={inputStyle}
                type="text"
            />

            {/* ── Product Description ── */}
            <label style={{ ...labelStyle, marginTop: "12px" }}>Product Description</label>
            <br />
            <div className="text-right">
                <textarea
                    onChange={handleChange}
                    name="description"
                    value={data.description}
                    placeholder="Describe the product batch for identification"
                    maxLength={120}
                    style={{
                        backgroundColor: "#F3F3F3",
                        border: "none",
                        borderRadius: "8px",
                        fontFamily: "Outfit",
                        width: "100%",
                        padding: "12px 17px",
                        resize: "none",
                    }}
                />
                <span style={{ ...hintStyle, textAlign: "right" }}>
                    {data.description?.length || 0}/120
                </span>
            </div>
        </CustomModal>
    );
};

export default EditBatch;
